import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { FormData } from "@amzn/aquila-service-type-script-client";
import {
    InsightOutputFormat,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty, isUndefined } from "lodash";
import React, { useEffect } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../../../api/cdbApi/cdbApi";
import { getG4SClient } from "../../../../api/g4sApi/g4sApi";
import type { FeedbackReason } from "../../../../api/g4sApi/types/operations";
import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import {
    G4S_FEEDBACK_REASONS_THUMBS_DOWN,
    G4S_FEEDBACK_REASONS_THUMBS_UP,
} from "../../../../constants/feedbackReasons";
import { G4sPlacement } from "../../../../constants/g4sPlacement";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../store/insightDetails";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { reportIssueStore } from "../../../../store/reportIssue";
import { workbenchStore } from "../../../../store/workbench";
import {
    AquilaThumbsFeedbackPlacement,
    AquilaThumbsFeedbackReaction,
} from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import type { DownloadAsset } from "../../../../types/common";
import type {
    FeedbackAttributes,
    FeedbackInsightIdentifier,
    FeedbackMetadata,
} from "../../../../types/feedback";
import { InsightPreviewFileFormats } from "../../../../types/insightPreviewFileFormats";
import { isLoading } from "../../../../utils/api.utils";
import { submitFeedback } from "../../../../utils/feedback.utils";
import { getMarketplaceId } from "../../../../utils/permission.utils";
import {
    logViewInsightDetailsClick,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../commonStyles";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

export interface InsightFromAICardActionProps {
    identifier: string;
    details: CardDetail[];
    title: string;
    selectedInsightSubtypeIds?: string[];
    responseId: string;
    index?: number;
}

export const InsightFromAICardAction: React.FunctionComponent<
    InsightFromAICardActionProps
> = ({
    identifier,
    title,
    details,
    selectedInsightSubtypeIds,
    index,
    responseId,
}) => {
    const insightType = InsightType.G4S;
    const marketplaceId = getMarketplaceId();
    const [setInsightDetails, setAsyncStatus] = useStore(
        insightDetailsStore,
        (state) => [state.setInsightDetails, state.setAsyncStatus],
    );
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const insightsData = filters[contentSubType.provider].data;
    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const [downloadState, downloadAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getInsightFamilyDownload({
            marketplaceId,
            familyInstanceId: identifier,
            selectedInsightSubtypeIds,
        });

        setDownloadAsset(data as DownloadAsset);
    });
    useEffect(() => {
        setAsyncStatus(downloadState.status);
    }, [downloadState, setAsyncStatus]);
    useAsyncErrorStatusNotification(downloadState);

    const [previewState, previewAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getInsightFamilyDownload({
            marketplaceId,
            familyInstanceId: identifier,
            selectedInsightSubtypeIds,
            outputFormat: InsightOutputFormat.PDF,
        });

        setPreviewAsset(data as DownloadAsset);
    });
    useAsyncErrorStatusNotification(previewState);

    const [feedbackState, feedbackAction] = useAsync(
        async (
            feedbackReasons: FeedbackReason[],
            freeFormFeedback: string,
            rating: number,
        ) => {
            await (
                await getG4SClient()
            ).createFeedback({
                responseId,
                feedbackReasons,
                freeFormFeedback,
                rating,
                dataContext: {
                    type: "ADVERTISER_INSIGHTS",
                    content: title,
                    id: identifier,
                },
                placement: G4sPlacement.EXPLORE_WITH_AI_PAGE,
            });
            upsertNotification(
                "Feedback provided",
                NotificationType.SUCCESS,
                identifier,
            );
        },
    );
    useAsyncErrorStatusNotification(feedbackState);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const handleInsightDetails = async (): Promise<void> => {
        logViewInsightDetailsClick(
            identifier,
            (index ?? 0) + 1,
            insightsData.length,
            undefined,
            useUnifiedSearch,
        );
        void previewAction.execute();
        void downloadAction.execute();
    };

    const feedbackInsightIdentifier: FeedbackInsightIdentifier = {
        identifier,
        insightType,
    };

    const feedbackMetadata: FeedbackMetadata = {
        responseId,
    };

    const handleFeedbackSubmit = (attr: FeedbackAttributes) => {
        const { reasons: selectedReasons, feedback, rating } = attr;
        const reasons = selectedReasons.map((reason) => ({ reason }));
        void feedbackAction.execute(reasons, feedback, rating);
        submitFeedback(feedbackInsightIdentifier, feedbackMetadata, rating);
        logWithTakt(TELEMETRY_ID.FEEDBACK_SUBMITTED, {
            reasons,
            feedback,
            rating,
            identifier,
            insightType,
            responseId,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.FEEDBACK_SUBMITTED,
            PHOENIX_FEATURE.FEEDBACK,
            {
                contentInfo: {
                    contentProvider: InsightType.G4S,
                    contentType: "",
                    contentId: identifier,
                },
                customData: {
                    reasons,
                    feedback,
                    rating,
                    responseId,
                },
            },
        );
    };

    const handleAquilaThumbsFeedbackSubmit = (
        identifier,
        insightType,
        placement,
        additionalInfo,
    ) => {
        const { feedbackReaction, formData } = additionalInfo;
        const reasons = (formData as FormData[]).find(
            (data) => data.field === "reasons",
        )?.value as string[];
        const selectedReasons = reasons.map((reason) => ({ reason }));
        const feedbackComment = (formData as FormData[]).find(
            (data) => data.field === "comment",
        )?.value as string;
        const feedbackRating =
            feedbackReaction === AquilaThumbsFeedbackReaction.THUMBS_UP ? 1 : 0;
        void feedbackAction.execute(
            selectedReasons,
            feedbackComment,
            feedbackRating,
        );
    };

    React.useEffect(() => {
        if (!isUndefined(downloadAsset)) {
            updateReportIssueContextData({
                identifier,
                title,
                insightType,
            });
            setInsightDetails({
                open: true,
                info: {
                    title,
                    details,
                    previewFileFormat: InsightPreviewFileFormats.PDF,
                    insightType,
                    shareInsightType: InsightType.INSIGHT,
                    identifier,
                    previewAsset,
                    downloadAsset,
                    description: "",
                    previewLoading: isLoading(previewState.status),
                    feedback: {
                        feedbackOptions: {
                            thumbsDown: {
                                handler: handleFeedbackSubmit,
                                reasons: G4S_FEEDBACK_REASONS_THUMBS_DOWN,
                            },
                            thumbsUp: {
                                handler: handleFeedbackSubmit,
                                reasons: G4S_FEEDBACK_REASONS_THUMBS_UP,
                            },
                        },
                        insightIdentifier: feedbackInsightIdentifier,
                        metadata: feedbackMetadata,
                        aquilaThumbsFeedbackSubmit:
                            handleAquilaThumbsFeedbackSubmit,
                    },
                },
            });
        }
    }, [previewAsset, downloadAsset, previewState.status]);
    const numSelectedSlides = getSlidesSelected(identifier).length;

    return (
        <StyledActionsContainer>
            <TextButton
                disabled={isEmpty(identifier)}
                onClick={() => void handleInsightDetails()}
            >
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={identifier}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && (
                <AquilaThumbsFeedback
                    identifier={identifier}
                    insightType={insightType}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                    configId={"G4sThumbsFeedbackConfig_StormV4"}
                    onSubmit={handleAquilaThumbsFeedbackSubmit}
                />
            )}
        </StyledActionsContainer>
    );
};
